import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { uploadDocx, validateDocxFile } from '../utils/storageUpload';
import {
  addQuizFile,
  createSession,
  getQuizFiles,
  syncQuizFilesFromStorage,
  QUIZ_DURATION_OPTIONS,
  DEFAULT_QUIZ_DURATION_MINUTES,
  normalizeQuizDurationMinutes,
} from '../utils/quizSessionManager';
import { getAssignableUsers } from '../utils/firebaseAuth';
import { toastManager } from '../utils/toastManager';
import { FileText, Users, CheckCircle2, AlertCircle, Upload, RefreshCw, Clock } from 'lucide-react';
import './CreateQuizSession.css';

const CreateQuizSession = ({ onSessionCreated, refreshTrigger = 0 }) => {
  const [quizSource, setQuizSource] = useState('existing'); // 'upload' | 'existing'
  const [quizFiles, setQuizFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [selectedUserEmails, setSelectedUserEmails] = useState([]);
  const [sessionTitle, setSessionTitle] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(DEFAULT_QUIZ_DURATION_MINUTES);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [files, users] = await Promise.all([
        getQuizFiles(),
        getAssignableUsers(),
      ]);
      setQuizFiles(files);
      setAllowedUsers(users || []);
      if (files.length > 0 && !selectedFile) {
        setSelectedFile(files[0]);
        if (files[0].durationMinutes != null) {
          setDurationMinutes(normalizeQuizDurationMinutes(files[0].durationMinutes));
        }
      }
    } catch (err) {
      console.error('Load data error:', err);
      setError('Failed to load quiz files and users');
      toastManager.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validationError = validateDocxFile(file);
      if (validationError) {
        toastManager.error(validationError);
        return;
      }
      setUploadFile(file);
      setError('');
    }
  };

  const handleSyncFromStorage = async () => {
    setSyncing(true);
    setError('');
    try {
      const added = await syncQuizFilesFromStorage();
      await loadData();
      if (added > 0) {
        toastManager.success(`Synced ${added} file(s) from Storage.`);
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.message || 'Failed to sync from Storage');
      toastManager.error('Failed to sync from Storage');
    } finally {
      setSyncing(false);
    }
  };

  const handleUploadNew = async () => {
    if (!uploadFile) {
      toastManager.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const userEmail = auth.currentUser?.email || 'unknown@example.com';
      const result = await uploadDocx(uploadFile, userEmail);

      await addQuizFile({
        downloadUrl: result.downloadUrl,
        path: result.path,
        fileName: uploadFile.name,
        uploadedBy: userEmail,
        durationMinutes,
      });

      setSelectedFile({
        id: 'new',
        downloadUrl: result.downloadUrl,
        fileName: uploadFile.name,
      });
      setQuizSource('existing');
      setUploadFile(null);
      await loadData();
      toastManager.success('File uploaded! You can now add users and create the session.');
    } catch (err) {
      setError(err.message || 'Upload failed');
      toastManager.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleUser = (email) => {
    setSelectedUserEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const selectAllUsers = () => {
    if (selectedUserEmails.length === allowedUsers.length) {
      setSelectedUserEmails([]);
    } else {
      setSelectedUserEmails([...allowedUsers]);
    }
  };

  const handleCreateSession = async () => {
    const quiz = quizSource === 'upload' ? null : selectedFile;
    const uploaded = quizSource === 'upload' && uploadFile ? null : selectedFile;

    let quizFileUrl, quizFileName;

    if (quizSource === 'existing' && selectedFile) {
      quizFileUrl = selectedFile.downloadUrl;
      quizFileName = selectedFile.fileName || selectedFile.quizFileName || 'Quiz';
    } else if (quizSource === 'upload' && uploadFile) {
      toastManager.error('Please upload the file first before creating the session.');
      return;
    } else {
      const fileMsg = quizFiles.length === 0
        ? 'No quiz files available. Upload one above or click Refresh to import from Storage.'
        : 'Please select or upload a quiz file';
      setError(fileMsg);
      toastManager.error(fileMsg);
      return;
    }

    if (!selectedUserEmails.length) {
      const userMsg = allowedUsers.length === 0
        ? 'No users available. Add users in User Management first.'
        : 'Please add at least one user to the session';
      setError(userMsg);
      toastManager.error(userMsg);
      return;
    }

    const userEmail = auth.currentUser?.email;
    if (!userEmail) {
      setError('You must be signed in to create a session');
      return;
    }

    setCreating(true);

    try {
      await createSession({
        quizFileUrl,
        quizFileName,
        assignedUserEmails: selectedUserEmails,
        createdBy: userEmail,
        title: sessionTitle || quizFileName,
        durationMinutes,
      });

      toastManager.success('Quiz session created! Assigned users can now see it.');
      setError('');
      setSelectedFile(quizFiles[0] || null);
      setSelectedUserEmails([]);
      setSessionTitle('');
      setDurationMinutes(DEFAULT_QUIZ_DURATION_MINUTES);
      if (onSessionCreated) onSessionCreated();
    } catch (err) {
      console.error('Session creation failed:', err);
      const msg = err?.message || err?.code || 'Failed to create session';
      setError(msg);
      toastManager.error(msg);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="create-session-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="create-quiz-session">
      <h3>Create Quiz Session</h3>
      <p className="create-session-desc">
        Choose or upload a quiz file, add users, and create a session. Only assigned users will see it.
      </p>

      {error && (
        <div className="create-session-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="create-session-form">
        {/* Step 1: Quiz source */}
        <div className="form-section">
          <label className="section-label">
            <FileText size={18} />
            <span className="section-label-text">Quiz file</span>
          </label>
          <div className="quiz-source-tabs">
            <button
              type="button"
              className={`tab-btn ${quizSource === 'existing' ? 'active' : ''}`}
              onClick={() => {
                setQuizSource('existing');
                if (quizFiles.length > 0 && !selectedFile) setSelectedFile(quizFiles[0]);
              }}
            >
              Choose existing
            </button>
            <button
              type="button"
              className={`tab-btn ${quizSource === 'upload' ? 'active' : ''}`}
              onClick={() => {
                setQuizSource('upload');
                setSelectedFile(null);
              }}
            >
              Upload new
            </button>
          </div>

          {quizSource === 'existing' ? (
            <div className="existing-files">
              <div className="existing-files-row">
                <select
                  value={selectedFile?.id || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const f = quizFiles.find((x) => x.id === id);
                    setSelectedFile(f || null);
                    if (f?.durationMinutes != null) {
                      setDurationMinutes(normalizeQuizDurationMinutes(f.durationMinutes));
                    }
                  }}
                  className="file-select"
                >
                  <option value="">{quizFiles.length === 0 ? 'No files—upload one above' : 'Select a file...'}</option>
                  {quizFiles.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.fileName || f.quizFileName || f.downloadUrl?.slice(-30) || f.id}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-refresh-files"
                  onClick={handleSyncFromStorage}
                  disabled={loading || syncing}
                  title="Sync Storage and refresh file list"
                >
                  {syncing || loading ? (
                    <span className="upload-spinner small"></span>
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  Refresh
                </button>
              </div>
              {quizFiles.length === 0 && (
                <p className="empty-hint">No quiz files in list. Upload one above, or click Refresh to import files from Storage.</p>
              )}
            </div>
          ) : (
            <div className="upload-section">
              <label className="file-input-label-inline">
                <input
                  type="file"
                  accept=".docx,.txt,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  onChange={handleFileSelect}
                  className="file-input-hidden"
                />
                <span className="browse-btn-inline">Choose file</span>
              </label>
              {uploadFile && (
                <span className="upload-file-name">{uploadFile.name}</span>
              )}
              <button
                type="button"
                className="btn-upload-inline"
                onClick={handleUploadNew}
                disabled={!uploadFile || uploading}
              >
                {uploading ? (
                  <>
                    <span className="upload-spinner small"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload
                  </>
                )}
              </button>
            </div>
          )}

          <div className="duration-select-wrap">
            <label className="section-label" htmlFor="quiz-duration">
              <Clock size={18} />
              <span className="section-label-text">Time duration</span>
            </label>
            <select
              id="quiz-duration"
              className="file-select duration-select"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(normalizeQuizDurationMinutes(e.target.value))}
            >
              {QUIZ_DURATION_OPTIONS.map((opt) => (
                <option key={opt.minutes} value={opt.minutes}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Session title */}
        <div className="form-section">
          <label className="section-label">Session title (optional)</label>
          <input
            type="text"
            value={sessionTitle}
            onChange={(e) => setSessionTitle(e.target.value)}
            placeholder={selectedFile?.fileName || 'My Quiz Session'}
            className="session-title-input"
          />
        </div>

        {/* Step 2: Users */}
        <div className="form-section">
          <label className="section-label">
            <Users size={18} />
            <span className="section-label-text">Assign users (only these users will see this session)</span>
            <button
              type="button"
              className="refresh-users-btn"
              onClick={loadData}
              disabled={loading}
              title="Refresh user list (use after adding users in User Management)"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </label>
          {allowedUsers.length === 0 ? (
            <p className="empty-hint">
              No users in User Management. Add users first, then assign them to sessions.
            </p>
          ) : (
            <>
              <button
                type="button"
                className="select-all-btn"
                onClick={selectAllUsers}
              >
                {selectedUserEmails.length === allowedUsers.length
                  ? 'Deselect all'
                  : 'Select all'}
              </button>
              <div className="user-checkboxes">
                {allowedUsers.map((email) => (
                  <label key={email} className="user-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedUserEmails.includes(email)}
                      onChange={() => toggleUser(email)}
                    />
                    <span>{email}</span>
                  </label>
                ))}
              </div>
              <p className="selected-count">
                {selectedUserEmails.length} user(s) selected
              </p>
            </>
          )}
        </div>

        <button
          type="button"
          className="btn-create-session"
          onClick={handleCreateSession}
          disabled={
            creating ||
            !selectedFile ||
            selectedUserEmails.length === 0
          }
        >
          {creating ? (
            <>
              <span className="upload-spinner small"></span>
              Creating...
            </>
          ) : (
            <>
              <CheckCircle2 size={18} />
              Create Session
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreateQuizSession;
